/**
 * transport/errorText.ts —— **异常文案唯一来源**（A-044①「按错误码分流文案」）。
 *
 * 纪律（写入即生效）：
 *  · 一切面向用户的异常提示文案**只写在本件**（禁散写：他处只传**数据位** `detail`，不带话术）；
 *  · **按错误码分流**：（★20261011：文案已全面用户人话；术语只留本件注释）仅 **WS/链路类错误码**（`LINK_CODES` ＝ `E_WS_CONNECT_FAILED`）的文案允许提示「查反代 WS 配置」；其余错误码各按其码给原因＋
 *    可操作动作，**一律不含「反代」字样**（真机实测反代 WS 正常 ⇒ 无关错误码不得带此提示）；
 *  · 未知错误码 ⇒ 显式兜底（不吞、不套链路话术）；
 *  · `E_CODES` 错误码闭集定义在本件（文案与码同源防漂移；`errors.ts` 再导出保持既有导入面）。
 *
 * ★F1（L-FE2-1，20261011）：用户可见文案**零技术原文**三件套（均收拢本件）：
 *  ① 异常→人话映射：`humanizeError()`（后端原文/异常串 ⇒ 人话；含裸库错、HTTP 串、码前缀剥离）；
 *  ② 声明 id/字段名→人话标签：`DECL_LABELS`＋`declLabel()`（呈现面禁直出 `view.*`／字段名）；
 *  ③ 设计口吻移出用户面：`scrubDesignVoice()`（「缺席：…零取数、不补造」等工程口吻 ⇒ 人话）。
 *  全仓 `E_` 码字面量只许出现在本件（他处一律经 `E_CODES` 引用）。
 *
 * 自证：`tests/errorText.routing.test.ts`（非 WS 错误码 ⇒ 不出现反代文案；禁散写静态扫描）。
 */

/** 错误码闭集（传输面自用词，不入事件词域）——文案表与本闭集一一对应（测试双向卡） */
export const E_CODES = {
  EGRESS_FORBIDDEN: "E_EGRESS_FORBIDDEN",
  WS_CONNECT_FAILED: "E_WS_CONNECT_FAILED",
  FRAME_INVALID: "E_FRAME_INVALID",
  SIGNER_MISSING: "E_SIGNER_MISSING",
  WRITE_UNAUTHORIZED: "E_WRITE_UNAUTHORIZED",
  WRITE_REJECTED: "E_WRITE_REJECTED",
  // ★F1（L-FE2-1）：全仓其余 E_ 码收拢本闭集（他处零裸串，一律经 E_CODES 引用）
  MODEL_WRITE: "E_MODEL_WRITE",
  AUTHN_FAILED: "E_AUTHN_FAILED",
  FILES_FAILED: "E_FILES_FAILED",
  SESSION_FAILED: "E_SESSION_FAILED",
  DECISION: "E_DECISION",
  INTERRUPT: "E_INTERRUPT",
  SHELL_DECL: "E_SHELL_DECL",
  VIEW_DECL: "E_VIEW_DECL",
  COMMAND_ACK: "E_COMMAND_ACK",
  FIELD_BINDING: "E_FIELD_BINDING",
  DIAGNOSTICS_EXPORT: "E_DIAGNOSTICS_EXPORT",
  UI_STRUCTURE: "E_UI_STRUCTURE",
  CATALOG_MISMATCH: "E_CATALOG_MISMATCH",
  PREFERENCES: "E_PREFERENCES",
  PREFERENCES_CONTEXT: "E_PREFERENCES_CONTEXT",
  // ★FE-E3（账行 L-PF-ASR-1／L-PF-ASR-2）：语音转写面（载体＝JSON 单形；失败显式，不静默）
  STT_FAILED: "E_STT_FAILED",
} as const;

/** WS/链路类错误码闭集（**只有**这些码的文案可提示「查反代 WS 配置」） */
export const LINK_CODES: ReadonlySet<string> = new Set<string>([
  E_CODES.WS_CONNECT_FAILED,
]);

/** 是否 WS/链路类错误码（反代提示的唯一放行口） */
export function isLinkCode(code: string): boolean {
  return LINK_CODES.has(code);
}

/**
 * 错误码 → 可操作提示（模板；`${detail}` 位由抛出点填**数据**，话术只在本件）。
 * 未知码 ⇒ `hintFor` 兜底（显式、不含反代）。
 */
const HINTS: Record<string, (detail: string) => string> = {
  // ★20261011：本表是**唯一**面向用户的异常文案来源 ⇒ 一律**用户人话**。
  //   实现/契约术语（CONTRACT §、反代 WS 配置、帧三型、caller/签名、入口侧）**不进 UI**，
  //   只作本件注释留痕（排障查这里，不看页面）。
  [E_CODES.WS_CONNECT_FAILED]: () =>
    "连接已断开，正在自动重连…",
  [E_CODES.FRAME_INVALID]: () =>
    "收到无法识别的内容，已忽略（不影响其它消息）",
  [E_CODES.EGRESS_FORBIDDEN]: (detail) =>
    "该请求不在允许范围内" + (detail ? "（" + detail + "）" : ""),
  [E_CODES.SIGNER_MISSING]: () =>
    "当前会话未就绪，请重新登录后再试",
  [E_CODES.WRITE_UNAUTHORIZED]: () =>
    "登录已失效，请重新登录",
  [E_CODES.WRITE_REJECTED]: (detail) =>
    "操作未被接受" + (detail ? "（" + detail + "）" : ""),
  // ★F1（L-FE2-1）新增：全仓 E_ 码各恰一条人话（文案闭集双向卡无漏）
  [E_CODES.MODEL_WRITE]: () =>
    "模型保存失败，请检查填写内容后重试",
  [E_CODES.AUTHN_FAILED]: () =>
    "登录已失效，请重新登录",
  [E_CODES.FILES_FAILED]: () =>
    "文件加载失败，请稍后重试",
  [E_CODES.SESSION_FAILED]: () =>
    "会话操作失败，请稍后重试",
  [E_CODES.DECISION]: () =>
    "决策操作未完成，请重试",
  [E_CODES.INTERRUPT]: () =>
    "打断操作未成功，请重试",
  [E_CODES.SHELL_DECL]: () =>
    "界面配置未就绪，请刷新页面后重试",
  [E_CODES.VIEW_DECL]: () =>
    "页面加载失败，请刷新后重试",
  [E_CODES.COMMAND_ACK]: () =>
    "命令未被接受，请重试",
  [E_CODES.FIELD_BINDING]: () =>
    "表单未能正确提交，请刷新页面后重试",
  [E_CODES.DIAGNOSTICS_EXPORT]: () =>
    "诊断信息导出失败，请重试",
  [E_CODES.UI_STRUCTURE]: () =>
    "页面结构异常，请刷新页面后重试",
  [E_CODES.CATALOG_MISMATCH]: () =>
    "页面组件未就绪，请刷新页面后重试",
  [E_CODES.PREFERENCES]: () =>
    "偏好设置保存失败，请重试",
  [E_CODES.PREFERENCES_CONTEXT]: () =>
    "偏好设置未就绪，请刷新页面后重试",
  // ★FE-E3（L-PF-ASR-1／L-PF-ASR-2）：语音转写（detail＝数据位："short"｜"absent"｜其余）
  //   旧数字码同义：40001「音频太短」⇒ short；50006「服务不可用」⇒ absent。
  [E_CODES.STT_FAILED]: (detail) =>
    detail === "short" ? "录音太短或没有声音，请重试"
      : detail === "absent" ? "语音识别服务暂不可用，请稍后重试"
        : "语音识别失败，请重试",
  // 排障对照（不进 UI）：WS_CONNECT_FAILED ⇒ 查 /stream 反代 Upgrade/Connection 头（CONTRACT §附）；
  //   FRAME_INVALID ⇒ 核对 CONTRACT.md §3 帧三型与 dto.schema.json；
  //   SIGNER_MISSING/WRITE_UNAUTHORIZED ⇒ caller/签名由入口侧注入（CONTRACT §6）。
};
/** 错误码 → 可操作提示（唯一取用口；未知码 ⇒ 显式兜底，不猜话术） */
export function hintFor(code: string, detail = ""): string {
  const make = HINTS[code];
  if (make) return make(detail);
  return "错误码 " + code + " 未收录文案（不猜话术）：请按码排查";
}

/**
 * 声明 id／字段名 → 人话标签（★F1 ②：呈现面禁直出声明 id／字段名）。
 * 闭集＝现网声明 id 与设置面字段名；未收录 ⇒ `declLabel` 显式兜底「该功能项」（不直出 id）。
 */
export const DECL_LABELS: Record<string, string> = {
  // 声明 id（视图/壳/组件）
  "view.customer_address_read": "客户地址",
  "settings.accounts": "账号设置",
  "main.composer.send": "发送按钮",
  "main.composer.takeover": "接管提示",
  "sidebar.new_session": "新建会话",
  "main.composer": "消息输入区",
  "settings": "设置",
  // 字段名（模型目录/设置面）
  api_model: "模型标识",
  display_name: "显示名称",
  base_url: "接口地址",
  api_key: "密钥",
  thinking_effort: "思考强度",
  max_output_tokens: "最大输出长度",
  context_length: "上下文长度",
  priority: "优先级",
  default_model: "默认模型",
  provider: "服务商",
  revision: "版本号",
};

/** 声明 id／字段名 → 人话标签（唯一取用口；未收录 ⇒ 显式兜底，不直出 id） */
export function declLabel(id: string): string {
  const hit = DECL_LABELS[id];
  if (hit) return hit;
  // 兜底：去技术前缀后逐段查表；仍无 ⇒ 「该功能项」（诚实兜底，不猜、不直出 id）
  const bare = id.replace(/^(view|main|sidebar|settings|field)\./, "");
  const seg = DECL_LABELS[bare];
  return seg ?? "该功能项";
}

/**
 * 设计口吻 → 人话（★F1 ③：「缺席：…零取数、不补造」等工程口吻移出用户面）。
 * 逐短语替换；替换后清理空括号与重复标点。
 */
const DESIGN_VOICE: Record<string, string> = {
  "零取数、不补造；不造数据、不假造行": "",
  "零取数、不补造": "",
  "不造数据、不假造行": "",
  "不假装可用": "",
  "不静默假成功": "",
  "不假成功": "",
  "禁静默假成功": "",
  "记残显示；": "",
  "记残显示": "",
  "缺席：": "数据缺席：",
  "如实呈现": "显示",
  "后端端点未就绪": "服务暂不可用",
  "⇒ 空列表": "",
  "空列表": "暂无数据",
  "不假造行": "",
  "入口禁用（不报错、不留空）": "入口已禁用",
};

/** 设计口吻清洗（唯一取用口；只做等义替换，不改事实位） */
export function scrubDesignVoice(text: string): string {
  let out = text;
  for (const [jargon, human] of Object.entries(DESIGN_VOICE)) {
    out = out.split(jargon).join(human);
  }
  return out
    .replace(/（\s*）/g, "")
    .replace(/；\s*；/g, "；")
    .replace(/：\s*；/g, "：")
    .replace(/\s{2,}/g, " ")
    .trim();
}

/**
 * 异常原文 → 人话（★F1 ①：后端原文/异常串不得直达用户面）。
 * 顺序：码前缀剥离（按码分流给 hint）→ 裸库错/关系缺失/视图 id → 人话＋标签 → HTTP 串 → 通用兜底；
 * 最后过一遍设计口吻清洗。事实位（id 标签）保留人话名，不直出技术串。
 */
export function humanizeError(raw: string): string {
  const text = String(raw ?? "");
  if (!text.trim()) return "操作未完成，请稍后重试";

  // ① 码前缀剥离：形如「E_XXX：…」且码已收录 ⇒ 按码分流人话（未收录码走下方模式表/兜底）
  const codeMatch = text.match(/^\s*(E_[A-Z_]{3,})[：:]\s*(.*)$/);
  if (codeMatch && (Object.values(E_CODES) as string[]).includes(codeMatch[1])) {
    return hintFor(codeMatch[1], "");
  }

  // ② 裸库错/关系缺失（UndefinedTable、relation "…" does not exist）⇒ 人话；内嵌视图 id 换人话标签
  if (/UndefinedTable|relation\s+"[^"]*"\s+does\s+not\s+exist|undefined\s+table/i.test(text)) {
    const idMatch = text.match(/view\.[a-z_]+/);
    const label = idMatch ? declLabel(idMatch[0]) : "相关数据";
    return label + " 暂时无法读取，请稍后重试（若持续出现请联系管理员）";
  }

  // ③ SQL/驱动串 ⇒ 人话
  if (/SQLSTATE|pg_[a-z_]+|syntax error at or near/i.test(text)) {
    return "数据处理出现异常，请稍后重试（若持续出现请联系管理员）";
  }

  // ④ 逐段替换（**不整段丢事实**）：视图 id 换人话标签；HTTP 串换人话；设计口吻清洗
  let out = text.replace(/view\.[a-z_]+/g, (id) => declLabel(id));
  out = out.replace(/HTTP\s+(\d{3})/g, "服务返回 $1");
  out = scrubDesignVoice(out);

  // ⑤ 残留技术串（点号路径／下划线标识／长拉丁 token／调用形）⇒ 整段换固定人话（不猜、不漏技术原文）
  if (/[A-Za-z_]{4,}\.[A-Za-z_]+|[a-z]+_[a-z_]+|[A-Z_]{4,}|\w+\(\)/.test(out)) {
    return "操作未完成，请稍后重试（若持续出现请联系管理员）";
  }
  return out.trim() || "操作未完成，请稍后重试";
}

/** 错误面渲染拼装口（toast/inline 唯一拼装处：错误码＋可操作提示） */
export function formatTransportError(shape: { code: string; hint: string }): string {
  return shape.code + "：" + shape.hint;
}
